import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSellerBoost, type SellerBoostItem } from '../../hooks/useSellerBoost';
import { getMarketplaceConnectionDisplayName } from '../../utils/marketplaceConnections';
import { AutomationRow, SaveStatus } from './settingsUi';

// Aba "Fila de impulsionar": escolhe os anuncios que entram no rodizio automatico
// da Shopee. Com a fila ligada, marcar/desmarcar salva sozinho.

type ShopeeConnection = { id: string; connectionName?: string | null; shopName?: string | null; shopId?: number; status?: string };
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export function SellerBoostSettingsView() {
  const [connections, setConnections] = useState<ShopeeConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SaveState>('idle');
  const timer = useRef<number>();
  const { data, loading, error, saveAutoBoost } = useSellerBoost(connectionId || undefined);
  const enabled = Boolean(data?.autoBoost?.enabled);

  useEffect(() => {
    fetch('/api/shopee/connections', { headers: headers() }).then((res) => res.json()).then((json) => {
      const list = (Array.isArray(json) ? json : (json.data || json.connections || [])).filter((item: ShopeeConnection) => item.status !== 'revoked');
      setConnections(list); setConnectionId((current) => current || list[0]?.id || '');
    }).catch(() => setConnections([]));
  }, []);

  useEffect(() => { setSelected(new Set(data?.autoBoost?.itemIds || [])); }, [connectionId, data?.autoBoost?.itemIds?.join(',')]);

  const persist = async (ids: Set<string>, on: boolean) => {
    setStatus('saving');
    try { await saveAutoBoost(Array.from(ids), on); setStatus('saved'); }
    catch (err: any) { setStatus('error'); toast.error(err.message || 'Erro ao salvar'); }
  };

  // A escolha salva sempre (ligada ou desligada): antes, com a fila desligada, marcar
  // anúncios não gravava nada e a seleção sumia ao fechar o painel.
  const updateSelection = (next: Set<string>) => {
    setSelected(next);
    window.clearTimeout(timer.current);
    // Fila ligada sem nenhum anúncio não tem o que rodar: desliga junto.
    const on = enabled && next.size > 0;
    if (enabled && next.size === 0) toast('Fila vazia: a fila automática foi desligada.');
    timer.current = window.setTimeout(() => persist(next, on), 800);
  };

  const toggleItem = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    updateSelection(next);
  };

  const toggleQueue = async (on: boolean) => {
    if (on && selected.size === 0) { toast.error('Adicione pelo menos um anúncio à fila antes de ligar.'); return; }
    window.clearTimeout(timer.current);
    await persist(selected, on);
  };

  const byId = useMemo(() => new Map((data?.items || []).map((i) => [i.itemId, i])), [data?.items]);
  const inQueue = useMemo(() => Array.from(selected).map((id) => byId.get(id)).filter(Boolean) as SellerBoostItem[], [selected, byId]);
  // Salvo na fila mas sem anúncio sincronizado: antes ficava invisível e só contava no número.
  const missing = useMemo(() => (data ? Array.from(selected).filter((id) => !byId.has(id)) : []), [data, selected, byId]);

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.items || []).filter((i) => !selected.has(i.itemId) && (!q || i.title.toLowerCase().includes(q) || i.itemId.includes(q) || (i.sku || '').toLowerCase().includes(q)));
  }, [data?.items, search, selected]);

  const row = (item: SellerBoostItem, action: ReactNode) => (
    <div key={item.itemId} className="flex items-center gap-3 rounded-md border border-gray-200 p-2.5">
      {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" className="h-9 w-9 rounded object-cover" /> : <span className="h-9 w-9 shrink-0 rounded bg-gray-100" />}
      <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-gray-900">{item.title}</span><span className="text-xs text-gray-500">{item.sku ? `SKU ${item.sku} · ` : ''}ID {item.itemId}{item.isBoosted ? ' · impulsionado agora' : ''}</span></span>
      {action}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        {connections.length > 1 ? (
          <select value={connectionId} onChange={(e) => setConnectionId(e.target.value)} className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs">
            {connections.map((c) => <option key={c.id} value={c.id}>{getMarketplaceConnectionDisplayName('shopee', c)}</option>)}
          </select>
        ) : <span />}
        <SaveStatus status={status} />
      </div>

      <AutomationRow
        title="Reimpulsionar automaticamente"
        help="A Shopee dá 5 vagas de destaque que duram 4 horas. A fila reocupa as vagas com os anúncios abaixo, em rodízio."
        enabled={enabled}
        onToggle={toggleQueue}
        fixedFrequency="a cada 4 horas"
        automationKey="boost"
      />

      {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className="rounded-xl border border-orange-200 bg-white">
        <div className="border-b border-orange-100 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">Na fila agora <span className="font-normal text-gray-500">({selected.size})</span></p>
          <p className="text-[11px] text-gray-500">{enabled ? 'Estes anúncios entram no rodízio.' : 'Fila salva, mas desligada: ligue acima para começar o rodízio.'}</p>
        </div>
        <div className="space-y-1.5 p-3">
          {loading && !data && <p className="py-4 text-center text-sm text-gray-500">Carregando a fila...</p>}
          {data && selected.size === 0 && <p className="py-4 text-center text-sm text-gray-500">Nenhum anúncio na fila. Adicione abaixo.</p>}
          {inQueue.map((item) => row(item, <button type="button" onClick={() => toggleItem(item.itemId)} className="shrink-0 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 hover:border-red-200 hover:text-red-600">Tirar</button>))}
          {missing.length > 0 && (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              {missing.length} anúncio(s) da fila não aparecem na lista sincronizada desta conta (ID {missing.join(', ')}). Sincronize os anúncios ou tire da fila:
              <button type="button" onClick={() => updateSelection(new Set(Array.from(selected).filter((id) => byId.has(id))))} className="ml-1 font-semibold underline">tirar estes</button>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">Adicionar à fila</p>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título, ID ou SKU" className="w-56 rounded-md border border-gray-200 px-2.5 py-1 text-xs focus:border-orange-300 focus:outline-none" />
        </div>
        <div className="max-h-[360px] space-y-1.5 overflow-y-auto p-3">
          {!loading && !(data?.items || []).length && <p className="py-6 text-center text-sm text-gray-500">{connections.length ? 'Nenhum anúncio sincronizado nesta conta.' : 'Nenhuma conta Shopee conectada.'}</p>}
          {available.map((item) => row(item, <button type="button" onClick={() => toggleItem(item.itemId)} className="shrink-0 rounded-md bg-orange-700 px-2 py-1 text-[11px] font-semibold text-white hover:bg-orange-800">Adicionar</button>))}
        </div>
      </section>
    </div>
  );
}
